import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { DashboardEconomyChangesResponse } from '@interfaces/dashboard/responses/dashboard-economy-changes.response';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { DashboardEconomyHistoryHttpService } from '@services/http/dashboard/dashboard-economy-history-http.service';
import { SharedModule } from '@shared/shared.module';
import { VaultDatePipe } from '@shared/pipes/vault-date.pipe';
import { ModalLoanDetailsComponent } from '@src/app/features/dashboard/costs/debt-management/pages/modal-loan-details/modal-loan-details.component';
import { subDays } from 'date-fns';
import { MenuItem } from 'primeng/api';
import { Subscription, finalize } from 'rxjs';
import { EconomyChangesBreakdownComponent } from '@src/app/features/dashboard/dashboard-management/pages/economy-history/components/economy-changes-breakdown/economy-changes-breakdown.component';
import {
  EconomyHistoryFiltersComponent,
  EconomyHistoryPeriod,
} from '@src/app/features/dashboard/dashboard-management/pages/economy-history/components/economy-history-filters/economy-history-filters.component';
import { EconomyHistoryTimelineComponent } from '@src/app/features/dashboard/dashboard-management/pages/economy-history/components/economy-history-timeline/economy-history-timeline.component';
import { EconomyMeasure } from '@src/app/features/dashboard/dashboard-management/pages/economy-history/economy-history.labels';

/** Período padrão ao abrir a tela (decisão do Tiago, 05/10/2026). */
const DEFAULT_PERIOD_DAYS = 30;

/**
 * Histórico da economia do dashboard (card 3295, redesenho no card 3323): "o que mudou" entre duas
 * datas e linha do tempo das versões. Aberta pela modal de operações do cenário de economia, com as
 * empresas que estavam selecionadas no dashboard (`?companyIds=`). Rota protegida pela flag
 * `dashboard-economy-history`.
 */
@Component({
  selector: 'app-economy-history',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    VaultDatePipe,
    EconomyHistoryFiltersComponent,
    EconomyChangesBreakdownComponent,
    EconomyHistoryTimelineComponent,
    ModalLoanDetailsComponent,
  ],
  templateUrl: './economy-history.component.html',
  styleUrls: ['./economy-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EconomyHistoryComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dateFormatter = inject(DateFormatterService);
  private readonly historyService = inject(DashboardEconomyHistoryHttpService);

  @ViewChild('loanDetailsModal') loanDetailsModal: ModalLoanDetailsComponent | null = null;

  readonly companyIds = signal<string[]>([]);
  readonly data = signal<DashboardEconomyChangesResponse | null>(null);
  readonly loading = signal(false);
  readonly error = signal(false);
  readonly measure = signal<EconomyMeasure>('inicial');
  readonly period = signal<EconomyHistoryPeriod>({
    from: this.dateFormatter.formatDateToYYYYMMDD(
      subDays(this.dateFormatter.getTodayDate(), DEFAULT_PERIOD_DAYS)
    ),
    to: this.dateFormatter.formatDateToYYYYMMDD(this.dateFormatter.getTodayDate()),
  });

  private request: Subscription | null = null;

  readonly activeMeasure = computed(() => {
    const d = this.data();
    if (!d) return null;
    return this.measure() === 'inicial' ? d.inicial : d.atual;
  });

  /** "Holding, Operações". */
  readonly companiesLabel = computed(() => {
    const names = this.data()?.companyNames ?? {};
    return this.companyIds()
      .map(id => names[id])
      .filter((n): n is string => !!n)
      .join(', ');
  });

  /** Caminho padrão: Dashboard (com as mesmas empresas) > Histórico da economia. */
  readonly breadcrumbHome = computed<MenuItem>(() => ({
    icon: 'pi pi-home',
    label: 'Dashboard',
    routerLink: '/dashboard/dashboard-management',
    queryParams: this.companyIds().length ? { companyIds: this.companyIds().join(',') } : undefined,
  }));
  readonly breadcrumbs: MenuItem[] = [{ label: 'Histórico da economia' }];

  readonly hasHistory = computed(() => (this.data()?.companiesWithHistory ?? 0) > 0);

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const ids = (params.get('companyIds') ?? '')
        .split(',')
        .map(id => id.trim())
        .filter(Boolean);
      this.companyIds.set(ids);
      this.load();
    });
  }

  load(): void {
    const ids = this.companyIds();
    if (!ids.length) {
      this.data.set(null);
      return;
    }

    // Filtro trocado com uma busca em andamento: a resposta antiga não pode sobrescrever a nova.
    this.request?.unsubscribe();
    this.loading.set(true);
    this.error.set(false);
    const { from, to } = this.period();
    this.request = this.historyService
      .getChanges(ids, from, to)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: result => this.data.set(result),
        error: () => this.error.set(true),
      });
  }

  onPeriodChange(period: EconomyHistoryPeriod): void {
    this.period.set(period);
    this.load();
  }

  setMeasure(measure: EconomyMeasure): void {
    this.measure.set(measure);
  }

  openLoan(loanOperationId: string): void {
    void this.loanDetailsModal?.open(loanOperationId);
  }
}
